import React, { useState, useEffect } from 'react'
import { useParams, withRouter } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { Flex, Box } from 'reflexbox'
import styled from 'styled-components'
import { Snackbar, Switch } from '@material-ui/core'
import set from 'set-value'

import * as surgeryActions from '../../redux/surgeries'
import DynamicForm from '../DynamicForm/DynamicForm'
import { Schema } from './InputSchema'
import HealtheraFooter from '../HealtheraFooter/HealtheraFooter'
import { HeaderContainer, HeaderTitle, BackButton, BounceAnimatedContainer, PaperContainer, ContainerRight } from '../../healtheraComponents'
import { validated } from '../../helpers'
import SurgeryMap from '../GoogleMap/SurgeryMap'
import EditableSelect from './components/EditableSelect';
import PublicProfileCard from './components/PublicProfileCard';

const SurgeryDetails = props => {

  const [surgeryDetails, setSurgeryDetails] = useState({})
  const [validationArray, setValidationArray] = useState([])
  const [schema, setSchema] = useState({})
  const {surgeryID} = useParams()
  const { surgery, error } = useSelector(state => state.surgeries)
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(surgeryActions.getSurgery(surgeryID));
    return () => {
      dispatch(surgeryActions.resetSingleSurgery());
    };
  }, [dispatch, surgeryID])

  useEffect(() => {
    if (surgery) {
      setSchema(Schema({ ...surgery }))
      setSurgeryDetails({ ...surgery })
    }
  }, [surgery])

  const onChange = async ({ field, value, validation = true }) => {
    set(validationArray, field, validation)
    set(surgeryDetails, field, value)
    setValidationArray(validationArray)

    const newSurgeryDetails = { ...surgeryDetails }
    setSurgeryDetails(newSurgeryDetails)
  }

  const handleClose = () => {
    dispatch(surgeryActions.resetError())
  }

  const goBack = () => {
    const { history, location } = props;
    const { state: { goBackLinks } = {} } = location;
    const lastItem = goBackLinks?.[goBackLinks?.length-1];
    if (/surgeries/.test(lastItem)) {
      return history.push(lastItem);
    }
    return history.push(`${process.env.PUBLIC_URL}/surgeries`);
  };

  const updateGpIntegration = async event => {
    if(event.nativeEvent.inputType) return
    dispatch(surgeryActions.updateSurgery(surgery._id, { integration_method: event.target.value === 'no_integration' ? null : event.target.value }))
  }

  const uppdateGpsocProvider = async (provider) => {
    if (!provider) return;
    await dispatch(surgeryActions.updateSurgery(surgery._id, { gpsoc: provider }));
  }

  const removeSpaces = value => value.replace(/\s/g, '');

  const handleSubmit = async event => {
    if (event) event.preventDefault();
    const update = {
      note: surgeryDetails.note,
      surgery_email: surgeryDetails.surgery_email,
      surgery_fax: surgeryDetails && surgeryDetails.surgery_fax ? removeSpaces(surgeryDetails.surgery_fax) : '',
      excluded: surgeryDetails.excluded,
    }
    await dispatch(surgeryActions.updateSurgery(surgeryID, update))
    await dispatch(surgeryActions.getSurgery(surgeryID))
  }

  const savePublicProfile = async update => {
    try {
      await dispatch(surgeryActions.updateSurgery(surgeryID, update))
      await dispatch(surgeryActions.getSurgery(surgeryID))
    } catch (err) {
      return undefined
    }
  }

  const updateExcluded = e => {
    onChange({ field:'excluded', value: e.target.checked })
    handleSubmit(e)
  }

  const {
    region, excluded, closed,
    integration_method, surgery_email, surgery_fax, gpsoc,
    no_of_patients, no_of_gpsoc_patients, no_of_repeats,
    lat, lon
  } = surgery || {};
  const valid = !validated(validationArray)

  return (
    <>
      {surgery && <BounceAnimatedContainer>
        <Snackbar open={error ? true : false}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={2000}
          message={error} />
        <HeaderContainer padding={`20px 0 20px 0`} borderbottom={`none`} p={2} align='center' justify='space-between'>
          <Flex align='center'>
            <BackButton padding={`0 5px 0 0`} onClick={goBack} />
            <HeaderTitle cursor={`pointer`} onClick={goBack} px={2} w={1 / 3}>Surgeries</HeaderTitle>
          </Flex>
          <Flex align='center'>
            <Box w={12 / 12}>Elected to be excluded</Box>
            <Box>
              <Switch onChange={updateExcluded} defaultChecked={!!excluded} />
            </Box>
          </Flex>
        </HeaderContainer>
        <PaperContainer elevation={0}>
          <HeaderContainer marginright={`0px`} padding={`0 0 20px 0`}>
            <DynamicForm
              title={surgery.surgery_name}
              margintop={`5px`}/>
            <Box>({!closed ? 'Open' : 'Closed'})</Box>
            <ContainerRight align='center'>
              <Box pr={2}><b>Region:</b> {region}</Box>
            </ContainerRight>
          </HeaderContainer>
          <Flex>
            <Box pt={2} pr={4} w={`50%`}>
              <Solid>
                <DynamicForm
                  title={'Address'}
                  margintop={`5px`}
                  onChange={onChange}
                  fields={schema.address} />
                <DynamicForm
                  title={'Organisational Code'}
                  margintop={`5px`}
                  onChange={onChange}
                  fields={schema.odsCode} />
              </Solid>
              <DynamicForm
                title={'Notes'}
                save={handleSubmit}
                editable
                displayButtons
                disabled={valid}
                margintop={`5px`}
                onChange={onChange}
                fields={schema.note} />
            </Box>
            <Box pt={2} pr={4} w={`50%`}>
              <SurgeryMap lat={lat} lon={lon}/>
              <DivWithTopMargin>
                <DynamicForm
                  title={'Surgery Contact Info'}
                  save={handleSubmit}
                  editable
                  displayButtons
                  disabled={valid}
                  margintop={`5px`}
                  onChange={onChange}
                  fields={schema.integration} />
              </DivWithTopMargin>
              <Title>GP Integration Methods</Title>
              <FormContainer id='integration_method' onChange={updateGpIntegration} onSubmit={handleSubmit}>
                <div>
                  <CheckBox
                    value='email' id='email' name='integration_method'
                    type='radio' disabled={!surgery_email}
                    defaultChecked={integration_method === 'email'}
                  />
                  <IntegrationLabel htmlFor='email'>Email</IntegrationLabel>
                  {!!surgery_email ? <span>{surgery_email}</span> : <GreyedOut>No Email</GreyedOut>}
                </div>
                <div>
                  <CheckBox
                    value='fax' id='fax' name='integration_method'
                    type='radio' disabled={!surgery_fax}
                    defaultChecked={integration_method === 'fax'}
                  />
                  <IntegrationLabel htmlFor='fax'>Fax</IntegrationLabel>
                  {!!surgery_fax ? <span>{surgery_fax}</span> : <GreyedOut>No Fax</GreyedOut>}
                </div>
                {!!gpsoc && <div>
                  <CheckBox
                    value='gpsoc' id='emis' name='integration_method'
                    type='radio' defaultChecked={integration_method === 'gpsoc'}
                  />
                  <IntegrationLabel htmlFor='emis'>GPSoC Mandatory</IntegrationLabel>
                </div>}
                <div>
                  <CheckBox
                    value={'no_integration'} id='no_integration' name='integration_method'
                    type='radio' defaultChecked={!integration_method}
                  />
                  <IntegrationLabel htmlFor='no_integration'>No Integration</IntegrationLabel>
                </div>
              </FormContainer>
              { gpsoc && 
                (<div>
                  <Title>GPSoC Provider</Title>
                  <EditableSelect
                    value={gpsoc}
                    options={['emis', 'tpp']}
                    label="Select GPSoC provider"
                    handleChange={uppdateGpsocProvider}>
                  </EditableSelect>
                </div>)
              }

              <SubTitle>No. of Patients: {no_of_patients}</SubTitle>
              { gpsoc && <SubTitle>No. of GPSoC integrated Patients: {no_of_gpsoc_patients}</SubTitle> }
              <SubTitle>No. of Repeats: {no_of_repeats}</SubTitle>

            </Box>
          </Flex>
          <PublicProfileCard surgery={surgery} onSave={savePublicProfile} />
        </PaperContainer>
        <HealtheraFooter />
      </BounceAnimatedContainer>}
    </>
  )
}

export default withRouter(SurgeryDetails)

const Title = styled.span`
  font-size: 16px;
  font-weight: 800;
  margin: 15px 0 10px 0;
  display: block;
`
const FormContainer = styled.form`
  flex-direction: row;
`
const CheckBox = styled.input`
:after {
  width: 15px;
  height: 15px;
  border-radius: 15px;
  top: -2px;
  left: -1px;
  position: relative;
  background-color: #d1d3d1;
  content: '';
  display: inline-block;
  visibility: visible;
  border: 2px solid white;
}
:checked:after {
  width: 15px;
  height: 15px;
  border-radius: 15px;
  top: -2px;
  left: -1px;
  position: relative;
  background-color: #2f9d6d;
  content: '';
  display: inline-block;
  visibility: visible;
  border: 2px solid white;
}
`

const Label = styled.label`
  margin-left: 10px;
  margin-right: 25px;
  margin-top: 5px;
  display: inline-block;
  width: 110px;
`;

const IntegrationLabel = styled(Label)`
  width: 150px;
`;

const SubTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  display: block;
  margin: 20px 0 10px 0;
`

const Solid = styled.div`
  overflow: visible;
  pointer-events: none;
  background:none !important;
`

const GreyedOut = styled.span`
  color: lightgrey;
`

const DivWithTopMargin = styled.div`
  margin-top: 1rem;
`
